import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { JobPosting } from '../types';
import { apiService } from '../services/api';
import { Loading } from '../components';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { ArrowLeft, Pencil, PackageX } from 'lucide-react';
import { CLOSE_REASON_PLATFORMS, CLOSE_REASON_OTHERS } from '../constants/closeReasons';
import { formatDate } from '../utils/date';

const MyJobPostingsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();

  const [jobPostings, setJobPostings] = useState<JobPosting[]>([]);
  const [loading, setLoading] = useState(true);
  const [legalMinimumWage, setLegalMinimumWage] = useState<number | null>(null);

  const [closingPosting, setClosingPosting] = useState<JobPosting | null>(null);
  const [foundHelper, setFoundHelper] = useState(false);
  const [closeReasons, setCloseReasons] = useState<Set<string>>(new Set());
  const [closing, setClosing] = useState(false);

  const defaultTab = (location.state as any)?.tab ?? 'all';

  useEffect(() => {
    if (authLoading || !user?.id || user.role !== 'EMPLOYER') return;
    Promise.all([
      apiService.jobPostings.getByEmployer(user.id, 0, 200).then((r: any) => {
        if (r.success && r.data) setJobPostings(r.data.content || []);
      }),
      apiService.jobPostings.getLegalMinimumWage().then((r: any) => {
        if (r?.success && r.data?.amount) setLegalMinimumWage(r.data.amount);
      }).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, [user, authLoading]);

  const formatCurrency = (amount?: number, currency = 'HKD') => {
    if (amount == null) return '-';
    return new Intl.NumberFormat('zh-HK', { style: 'currency', currency, maximumFractionDigits: 0 }).format(amount);
  };

  const formatSalary = (posting: JobPosting) => {
    if (posting.salaryType === 'FIXED' && posting.fixedSalary) return formatCurrency(posting.fixedSalary, posting.salaryCurrency || 'HKD');
    if (posting.salaryType === 'RANGE' && (posting.minSalary || posting.maxSalary))
      return `${formatCurrency(posting.minSalary, posting.salaryCurrency || 'HKD')} - ${formatCurrency(posting.maxSalary, posting.salaryCurrency || 'HKD')}`;
    if (posting.salaryType === 'OTHER') {
      if (posting.salaryOtherOption === 'LEGAL_MINIMUM') return legalMinimumWage ? t('jobPostings.my.salary.legalMinimumWith', { amount: formatCurrency(legalMinimumWage) }) : t('jobPostings.my.salary.legalMinimum', '法定最低工資');
      if (posting.salaryOtherOption === 'TO_BE_DISCUSS') return t('jobPostings.my.salary.toBeDiscussed', '面議');
    }
    return '—';
  };

  const renderStatusBadge = (status: string) => {
    const map: Record<string, { label: string; className: string }> = {
      ACTIVE: { label: t('common.active'), className: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700' },
      CLOSED: { label: t('common.closed'), className: 'border-red-500/20 bg-red-500/10 text-red-700' },
      DRAFT: { label: t('jobPosting.status.draft', '草稿'), className: 'border-amber-500/30 bg-amber-500/10 text-amber-700' },
    };
    const s = map[status] ?? { label: status, className: '' };
    return <Badge variant="outline" className={s.className}>{s.label}</Badge>;
  };

  const openCloseDialog = (posting: JobPosting) => {
    setClosingPosting(posting);
    setFoundHelper(false);
    setCloseReasons(new Set());
  };

  const toggleReason = (code: string) => {
    setCloseReasons(prev => {
      const next = new Set(prev);
      next.has(code) ? next.delete(code) : next.add(code);
      return next;
    });
  };

  const submitClose = useCallback(async () => {
    if (!closingPosting) return;
    setClosing(true);
    const reasonStr = Array.from(closeReasons).join(',');
    try {
      await apiService.jobPostings.updateStatus(closingPosting.id, 'CLOSED', reasonStr || undefined);
      setJobPostings(prev => prev.map(p => p.id === closingPosting.id ? { ...p, status: 'CLOSED' as const } : p));
      setClosingPosting(null);
    } finally {
      setClosing(false);
    }
  }, [closingPosting, closeReasons]);

  if (!authLoading && (!user || user.role !== 'EMPLOYER')) return <Navigate to="/home" replace />;
  if (authLoading || loading) return (
    <div className="flex min-h-[calc(100vh-64px)] items-center justify-center">
      <Loading message={t('common.loading', '載入中...')} />
    </div>
  );

  const renderList = (postings: JobPosting[]) => postings.length === 0 ? (
    <div className="rounded-2xl border border-dashed border-border bg-muted/20 px-4 py-12 text-center text-sm text-muted-foreground">
      {t('jobPostings.my.noJobsInCategory', '此分類暫無職位。')}
    </div>
  ) : postings.map((posting) => (
    <div
      key={posting.id}
      className="flex items-start justify-between gap-4 rounded-2xl border border-border/70 bg-background px-4 py-4 transition hover:border-primary/40 hover:bg-primary/5"
    >
      <div
        role="button"
        tabIndex={0}
        onClick={() => navigate(`/job-postings/${posting.id}/detail`)}
        onKeyDown={(e) => e.key === 'Enter' && navigate(`/job-postings/${posting.id}/detail`)}
        className="min-w-0 flex-1 cursor-pointer space-y-2 text-left"
      >
        <div className="flex flex-wrap items-center gap-2">
          <p className="font-medium text-foreground break-words">{posting.headline || posting.title}</p>
          {renderStatusBadge(posting.status)}
        </div>
        <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
          <span>{posting.location}</span>
          <span>•</span>
          <span>{posting.workType === 'FULL_TIME' ? t('jobPostings.my.workType.fullTime', '全職') : posting.workType === 'PART_TIME' ? t('jobPostings.my.workType.partTime', '兼職') : t('jobPostings.my.workType.temporary', '臨時')}</span>
          <span>•</span>
          <span>{formatDate(posting.postedDate) || '—'}</span>
        </div>
        <p className="line-clamp-2 max-w-2xl break-words text-sm text-muted-foreground">{posting.description}</p>
        <p className="text-sm font-medium text-foreground">{formatSalary(posting)}</p>
      </div>
      <div className="flex shrink-0 flex-col gap-2">
        <Button variant="outline" size="sm" className="gap-1.5" onClick={() => navigate(`/job-postings/${posting.id}/edit`)}>
          <Pencil className="h-3.5 w-3.5" />{t('jobPostings.my.actions.edit', '修改')}
        </Button>
        {posting.status !== 'CLOSED' && (
          <Button variant="outline" size="sm" className="gap-1.5 text-destructive hover:text-destructive" onClick={() => openCloseDialog(posting)}>
            <PackageX className="h-3.5 w-3.5" />{t('jobPostings.my.actions.close', '下架')}
          </Button>
        )}
      </div>
    </div>
  ));

  return (
    <div className="space-y-6">
      <Button variant="ghost" className="gap-2" onClick={() => navigate(-1)}>
        <ArrowLeft className="h-4 w-4" />{t('common.back', '返回')}
      </Button>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle className="text-xl">{t('jobPostings.my.title', '管理職位')}（{jobPostings.length}）</CardTitle>
            <p className="text-sm text-muted-foreground">{t('jobPostings.my.subtitle', '管理及追蹤已發佈的職位')}</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => navigate('/job-postings/new')}>
            {t('jobPostings.my.addJob', '+ 新增職位')}
          </Button>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue={defaultTab}>
            <TabsList className="mb-4 w-full overflow-x-auto flex-wrap h-auto gap-1">
              <TabsTrigger value="all">{t('jobPostings.my.tabs.all', '全部')}（{jobPostings.length}）</TabsTrigger>
              <TabsTrigger value="active">{t('common.active')}（{jobPostings.filter(p => p.status === 'ACTIVE').length}）</TabsTrigger>
              <TabsTrigger value="closed">{t('common.closed')}（{jobPostings.filter(p => p.status === 'CLOSED').length}）</TabsTrigger>
              <TabsTrigger value="draft">{t('jobPostings.my.tabs.draft', '草稿')}（{jobPostings.filter(p => p.status === 'DRAFT').length}）</TabsTrigger>
            </TabsList>
            <TabsContent value="all" className="space-y-3">{renderList(jobPostings)}</TabsContent>
            <TabsContent value="active" className="space-y-3">{renderList(jobPostings.filter(p => p.status === 'ACTIVE'))}</TabsContent>
            <TabsContent value="closed" className="space-y-3">{renderList(jobPostings.filter(p => p.status === 'CLOSED'))}</TabsContent>
            <TabsContent value="draft" className="space-y-3">{renderList(jobPostings.filter(p => p.status === 'DRAFT'))}</TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* 下架對話框 */}
      <Dialog open={!!closingPosting} onOpenChange={(o) => { if (!o) setClosingPosting(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('hireCheck.closeJobTitle', '下架職位')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">{t('hireCheck.chooseReason', '請選擇下架原因（可多選）')}</p>
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('hireCheck.channelQuestion', '透過哪個渠道找到外傭？')}</p>
              {CLOSE_REASON_PLATFORMS.map(r => (
                <label key={r.code} className="flex cursor-pointer items-center gap-3 rounded-lg p-2 hover:bg-muted/40">
                  <Checkbox checked={closeReasons.has(r.code)} onCheckedChange={() => toggleReason(r.code)} />
                  <span className="text-sm">{t(r.labelKey, r.label)}</span>
                </label>
              ))}
            </div>
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('hireCheck.otherReasons', '其他原因')}</p>
              {CLOSE_REASON_OTHERS.map(r => (
                <label key={r.code} className="flex cursor-pointer items-center gap-3 rounded-lg p-2 hover:bg-muted/40">
                  <Checkbox checked={closeReasons.has(r.code)} onCheckedChange={() => toggleReason(r.code)} />
                  <span className="text-sm">{t(r.labelKey, r.label)}</span>
                </label>
              ))}
            </div>
            <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-primary/20 bg-primary/5 p-3">
              <Checkbox checked={foundHelper} onCheckedChange={(v) => setFoundHelper(!!v)} />
              <span className="text-sm font-medium">{t('hireCheck.foundSuitable', '已找到合適外傭')}</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setClosingPosting(null)}>{t('common.cancel', '取消')}</Button>
            <Button variant="destructive" onClick={submitClose} disabled={closing}>
              {closing ? t('hireCheck.processing', '處理中...') : t('hireCheck.confirmClose', '確認下架')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyJobPostingsPage;
